<template>
	<div class="admin-users">
		<div v-if="loading" class="loading">
			{{ $t("adminPanelPage.loading") }}
		</div>

		<div v-else-if="error" class="error">
			{{ error }}
		</div>

		<div v-else class="admin-content">
			<div class="users-section">
				<div class="toolbar">
					<label class="search">
						<AppIcon :name="IconName.Search" :size="14" />
						<input v-model="search" type="search" :placeholder="$t('adminPanelPage.users.searchPlaceholder')" />
					</label>
					<Button
						v-if="canCreateUser"
						class="create-user"
						variant="primary"
						size="m"
						shape="pill"
						@click="openCreateUserModal"
					>
						<AppIcon :name="IconName.Plus" :size="14" />
						{{ $t("adminPanelPage.users.createUser") }}
					</Button>
					<div v-if="canCreateTestOpportunity || canCommunicate" class="actions">
						<Button
							v-if="canCreateTestOpportunity"
							variant="secondary"
							size="m"
							shape="pill"
							@click="openCreateOpportunitiesModal"
						>
							{{ $t("adminPanelPage.users.createOpportunities") }}
						</Button>
						<Button
							v-if="canCreateNotification"
							variant="secondary"
							size="m"
							shape="pill"
							@click="openCreateNotificationModal"
						>
							{{ $t("adminPanelPage.users.createNotification") }}
						</Button>
						<Button v-if="canCreateEmail" variant="secondary" size="m" shape="pill" @click="openCreateEmailModal">
							{{ $t("adminPanelPage.users.createEmail") }}
						</Button>
						<Button v-if="canSendSms" variant="secondary" size="m" shape="pill" @click="openSendSmsModal">
							{{ $t("adminPanelPage.users.sendSms") }}
						</Button>
					</div>
				</div>
				<div class="users-grid">
					<div class="header">
						<div class="cell header user">{{ $t("adminPanelPage.users.user") }}</div>
						<div class="cell header">{{ $t("adminPanelPage.users.roles") }}</div>
						<div class="cell header date">{{ $t("adminPanelPage.users.memberSince") }}</div>
						<div class="cell header date">{{ $t("adminPanelPage.users.updatedBy") }}</div>
						<div class="cell header action">{{ $t("adminPanelPage.users.actions") }}</div>
					</div>

					<div v-for="user in filteredUsers" :key="user.id" class="row" :class="{ archived: !user.isActive }">
						<div class="cell user">
							<div class="user-identity">
								<span class="name">
									{{ user.name }}
									<span v-if="hasExtendedAccess(user.id)" class="extended-access-badge">
										{{ $t("adminPanelPage.users.extendedAccess") }}
									</span>
								</span>
								<span class="email">{{ user.email }}</span>
							</div>
						</div>
						<div class="cell roles">
							<div class="roles-with-action">
								<ArkListboxDropdown
									:model-value="userRoles[user.id]"
									@update:model-value="value => handleRoleChange(user.id, value as number[])"
									:options="roleOptions"
									:multiple="true"
									:disabled="updatingUser === user.id"
									:clearable="true"
									:searchable="false"
									placeholder="Select roles"
									class="role-multiselect"
								/>
								<span v-if="updatingUser === user.id" class="updating">{{ $t("adminPanelPage.users.updating") }}</span>
								<span v-else-if="updateSuccess === user.id" class="success">{{
									$t("adminPanelPage.users.updated")
								}}</span>
								<button
									v-else
									type="button"
									class="permissions-icon-btn"
									:title="$t('adminPanelPage.userPermissionsModal.title')"
									:aria-label="$t('adminPanelPage.userPermissionsModal.title')"
									@click="showUserPermissions(user)"
								>
									<AppIcon :name="IconName.ShieldPin" :size="18" />
								</button>
							</div>
						</div>
						<div class="cell date" :data-label="$t('adminPanelPage.users.memberSince')">
							{{ formatDate(user.createdAt) }}
						</div>
						<div class="cell updated-by" :data-label="$t('adminPanelPage.users.updatedBy')">
							{{ getUpdatedByUserName(user.updatedBy) }}
						</div>
						<div class="cell action">
							<div class="user-actions-wrap">
								<Popover.Root
									:open="openActionsMenuUserId === user.id"
									@update:open="open => (openActionsMenuUserId = open ? user.id : null)"
									:positioning="{ placement: 'bottom-end', gutter: 4 }"
								>
									<Popover.Trigger as-child>
										<button type="button" class="trigger" :disabled="userActionsBusy(user)">
											<span class="label">{{
												userActionsBusy(user)
													? $t("adminPanelPage.users.actionInProgress")
													: $t("adminPanelPage.users.actions")
											}}</span>
											<span class="chevron" :class="{ open: openActionsMenuUserId === user.id }"></span>
										</button>
									</Popover.Trigger>
									<Popover.Positioner>
										<Popover.Content class="user-actions-popover">
											<button
												v-if="canLoginAs"
												type="button"
												class="item"
												:disabled="loggingInAs === user.id"
												@click="closeActionsMenuAnd(() => handleLoginAs(user))"
											>
												{{ $t("adminPanelPage.users.loginAs") }}
											</button>
											<button
												type="button"
												class="item danger"
												:disabled="revokingUser === user.id || !user.isActive"
												@click="closeActionsMenuAnd(() => handleRevokeToken(user))"
											>
												{{ $t("adminPanelPage.users.revokeToken") }}
											</button>
											<button
												type="button"
												class="item danger"
												:disabled="archivingUser === user.id || !user.isActive"
												:title="
													user.isActive
														? $t('adminPanelPage.users.archiveUser')
														: $t('adminPanelPage.users.alreadyArchived')
												"
												@click="closeActionsMenuAnd(() => handleArchiveUser(user))"
											>
												{{ $t("adminPanelPage.users.archiveUser") }}
											</button>
										</Popover.Content>
									</Popover.Positioner>
								</Popover.Root>
								<span v-if="revokeSuccess === user.id" class="revoke-success-inline">{{
									$t("adminPanelPage.users.tokenRevoked")
								}}</span>
							</div>
						</div>
					</div>
					<div v-if="filteredUsers.length === 0" class="empty">{{ $t("adminPanelPage.users.noResults") }}</div>
				</div>
			</div>
		</div>
		<UserPermissionsModal
			:is-open="showPermissionsModal"
			:user="selectedUser"
			:permissions="userPermissions"
			:loading="loadingPermissions"
			@close="closePermissionsModal"
		/>
		<CreateUserModal
			:is-open="showCreateUserModal"
			:role-options="roleOptions"
			:saving="creatingUser"
			:submit-error="createUserError"
			@close="closeCreateUserModal"
			@save="handleCreateUser"
		/>
		<CreateNotificationModal
			:is-open="showCreateNotificationModal"
			:user-options="userOptions"
			:saving="creatingNotification"
			:submit-error="createNotificationError"
			translation-key="adminPanelPage.createNotificationModal"
			@close="closeCreateNotificationModal"
			@save="handleCreateNotification"
		/>
		<CreateNotificationModal
			:is-open="showCreateEmailModal"
			:user-options="userOptions"
			:saving="creatingEmail"
			:submit-error="createEmailError"
			translation-key="adminPanelPage.createEmailModal"
			@close="closeCreateEmailModal"
			@save="handleCreateEmail"
		/>
		<SendSmsModal
			:is-open="showSendSmsModal"
			:user-options="userOptions"
			:saving="sendingSms"
			:submit-error="sendSmsError"
			:backend-ready="true"
			@close="closeSendSmsModal"
			@save="handleSendSms"
		/>
		<CreateTestOpportunitiesModal
			:is-open="showCreateOpportunitiesModal"
			:saving="creatingOpportunities"
			:submit-error="createOpportunitiesError"
			:created-deal-ids="createdOpportunityDealIds"
			translation-key="adminPanelPage.createOpportunitiesModal"
			@close="closeCreateOpportunitiesModal"
			@save="handleCreateOpportunities"
		/>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { Permission, UseRootStore } from "@/model/RootStore"
import { storeToRefs } from "pinia"
import {
	getRoles,
	assignUserRoles,
	revokeUserToken,
	loginAsUser,
	archiveUser,
	createUser as createUserApi,
	createNotification as createNotificationApi,
	createEmail as createEmailApi,
	sendSms as sendSmsApi,
	createTestOpportunities as createTestOpportunitiesApi,
	type CreateEmailInput,
	type CreateNotificationInput,
	type SendSmsInput,
	type CreateTestOpportunitiesInput,
} from "@/utils/ApiClient"
import { Popover } from "@ark-ui/vue"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import Button from "@/components/ui/Button.vue"
import UserPermissionsModal from "@/components/admin/UserPermissionsModal.vue"
import CreateUserModal from "@/components/admin/CreateUserModal.vue"
import CreateNotificationModal from "@/components/admin/CreateNotificationModal.vue"
import SendSmsModal from "@/components/admin/SendSmsModal.vue"
import CreateTestOpportunitiesModal from "@/components/admin/CreateTestOpportunitiesModal.vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useToastStore } from "@/stores/toastStore.ts"
import { useAdminUserStore } from "@/stores/adminUserStore"
import { ExtendedAccessRoleName } from "@/types/extendedAccess"
import { sortRoles } from "@/utils/roles"

const { t } = useI18n()
const toastStore = useToastStore()
const router = useRouter()
const store = UseRootStore()

const adminUserStore = useAdminUserStore()
const { users } = storeToRefs(adminUserStore)
const roles = ref<any[]>([])
const userRoles = ref<Record<string, number[]>>({})
const loading = ref(true)
const error = ref("")
const updatingUser = ref<string | null>(null)
const updateSuccess = ref<string | null>(null)
const revokingUser = ref<string | null>(null)
const revokeSuccess = ref<string | null>(null)
const loggingInAs = ref<string | null>(null)
const archivingUser = ref<string | null>(null)
const openActionsMenuUserId = ref<string | null>(null)
const showCreateUserModal = ref(false)
const creatingUser = ref(false)
const createUserError = ref("")
const showCreateNotificationModal = ref(false)
const creatingNotification = ref(false)
const createNotificationError = ref("")
const showCreateEmailModal = ref(false)
const creatingEmail = ref(false)
const createEmailError = ref("")
const showSendSmsModal = ref(false)
const sendingSms = ref(false)
const sendSmsError = ref("")
const showCreateOpportunitiesModal = ref(false)
const creatingOpportunities = ref(false)
const createOpportunitiesError = ref("")
const createdOpportunityDealIds = ref<string[]>([])
const search = ref("")

const filteredUsers = computed(() => {
	const query = search.value.trim().toLowerCase()
	if (!query) return users.value
	return users.value.filter(
		user => user.name?.toLowerCase().includes(query) || user.email?.toLowerCase().includes(query)
	)
})

const userActionsBusy = (user: { id: string }) =>
	revokingUser.value === user.id || archivingUser.value === user.id || loggingInAs.value === user.id

const closeActionsMenuAnd = (fn: () => void) => {
	openActionsMenuUserId.value = null
	fn()
}

// Permissions modal state
const showPermissionsModal = ref(false)
const selectedUser = ref<any>(null)
const userPermissions = ref<any[]>([])
const loadingPermissions = ref(false)

const formatDate = (dateString: string | null | undefined): string => {
	if (!dateString) return "-"
	try {
		const date = new Date(dateString)
		return date.toLocaleDateString()
	} catch {
		return "-"
	}
}

const getUpdatedByUserName = (updatedBy: any): string => {
	if (!updatedBy) return "-"
	return updatedBy.name || updatedBy.email || "-"
}

const roleOptions = computed(() => {
	return roles.value.map(role => ({
		value: Number(role.id),
		label: role.name,
	}))
})

const extendedAccessRoleId = computed(() => {
	const role = roles.value.find(role => role.name === ExtendedAccessRoleName)
	return role ? Number(role.id) : null
})

function hasExtendedAccess(userId: string): boolean {
	return extendedAccessRoleId.value !== null && (userRoles.value[userId] ?? []).includes(extendedAccessRoleId.value)
}

const canLoginAs = computed(() => store.currentUser?.has(Permission.AuthorizationWrite) ?? false)
const canCreateUser = computed(() => store.currentUser?.has(Permission.UserCreate) ?? false)
const canCreateNotification = computed(() => store.currentUser?.has(Permission.NotificationCreate) ?? false)
const canCreateEmail = computed(() => store.currentUser?.has(Permission.EmailCreate) ?? false)
const canSendSms = computed(() => store.currentUser?.has(Permission.SmsCreate) ?? false)
const canCreateTestOpportunity = computed(() => store.currentUser?.has(Permission.CreateTestOpportunity) ?? false)
const canCommunicate = computed(() => canCreateNotification.value || canCreateEmail.value || canSendSms.value)
const userOptions = computed(() =>
	users.value
		.filter(user => user.isActive !== false)
		.map(user => ({
			value: Number(user.id),
			label: `${user.name || user.email || user.id} (${user.email || user.id})`,
		}))
)

const openCreateUserModal = () => {
	createUserError.value = ""
	showCreateUserModal.value = true
}

const closeCreateUserModal = () => {
	showCreateUserModal.value = false
	createUserError.value = ""
}

const openCreateNotificationModal = () => {
	createNotificationError.value = ""
	showCreateNotificationModal.value = true
}

const closeCreateNotificationModal = () => {
	showCreateNotificationModal.value = false
	createNotificationError.value = ""
}

const openCreateEmailModal = () => {
	createEmailError.value = ""
	showCreateEmailModal.value = true
}

const closeCreateEmailModal = () => {
	showCreateEmailModal.value = false
	createEmailError.value = ""
}

const openSendSmsModal = () => {
	sendSmsError.value = ""
	showSendSmsModal.value = true
}

const closeSendSmsModal = () => {
	showSendSmsModal.value = false
	sendSmsError.value = ""
}

const openCreateOpportunitiesModal = () => {
	createOpportunitiesError.value = ""
	createdOpportunityDealIds.value = []
	showCreateOpportunitiesModal.value = true
}

const closeCreateOpportunitiesModal = () => {
	showCreateOpportunitiesModal.value = false
	createOpportunitiesError.value = ""
	createdOpportunityDealIds.value = []
}

const loadUsers = async ({ force = true }: { force?: boolean } = {}) => {
	try {
		await adminUserStore.load({ force })
		for (const user of users.value) {
			userRoles.value[user.id] = user.roles?.map((r: any) => Number(r.id)) ?? []
		}
	} catch (err: any) {
		console.error("Error loading users:", err)
		error.value = err.message || "Failed to load users"
	}
}

const loadRoles = async () => {
	try {
		const response = await getRoles(store.userApiToken)
		roles.value = sortRoles(response)
	} catch (err: any) {
		console.error("Error loading roles:", err)
		error.value = err.message || "Failed to load roles"
	}
}

const handleRoleChange = async (userId: string, selectedRoleIds: number[]) => {
	userRoles.value[userId] = selectedRoleIds
	updatingUser.value = userId
	updateSuccess.value = null

	try {
		const numericRoleIds = selectedRoleIds.map(id => Number(id))
		if (numericRoleIds.length === 0) {
			console.log("Clearing all roles for user:", userId)
		}

		const result = await assignUserRoles(store.userApiToken, Number(userId), numericRoleIds)
		console.log("Roles assigned result:", result)

		await loadUsers()

		updateSuccess.value = userId

		setTimeout(() => {
			if (updateSuccess.value === userId) {
				updateSuccess.value = null
			}
		}, 3000)
	} catch (err: any) {
		console.error("Error updating user roles:", err)
		error.value = err.message || "Failed to update user roles"
	} finally {
		updatingUser.value = null
	}
}

const handleCreateUser = async (input: { firstName: string; lastName: string; email: string; roleId: number }) => {
	creatingUser.value = true
	createUserError.value = ""
	error.value = ""

	try {
		await createUserApi(store.userApiToken, input)
		await loadUsers()
		closeCreateUserModal()
		toastStore.success("adminPanelPage.users.createSuccess")
	} catch (err: any) {
		console.error("Error creating user:", err)
		createUserError.value = err.message || "Failed to create user"
	} finally {
		creatingUser.value = false
	}
}

const handleCreateNotification = async (input: CreateNotificationInput) => {
	creatingNotification.value = true
	createNotificationError.value = ""
	error.value = ""

	try {
		await createNotificationApi(store.userApiToken, input)
		closeCreateNotificationModal()
		toastStore.success("adminPanelPage.users.createNotificationSuccess")
		await store.fetchUnreadNotificationCount()
	} catch (err: any) {
		console.error("Error creating notification:", err)
		createNotificationError.value = err.message || "Failed to create notification"
	} finally {
		creatingNotification.value = false
	}
}

const handleCreateEmail = async (input: CreateEmailInput) => {
	creatingEmail.value = true
	createEmailError.value = ""
	error.value = ""

	try {
		await createEmailApi(store.userApiToken, input)
		closeCreateEmailModal()
		toastStore.success("adminPanelPage.users.createEmailSuccess")
	} catch (err: any) {
		console.error("Error creating email:", err)
		createEmailError.value = err.message || "Failed to create email"
	} finally {
		creatingEmail.value = false
	}
}

const handleSendSms = async (input: SendSmsInput) => {
	sendingSms.value = true
	sendSmsError.value = ""
	error.value = ""

	try {
		await sendSmsApi(store.userApiToken, input)
		closeSendSmsModal()
		toastStore.success("adminPanelPage.users.sendSmsSuccess")
	} catch (err: any) {
		console.error("Error sending SMS:", err)
		sendSmsError.value = err.message || "Failed to send SMS"
	} finally {
		sendingSms.value = false
	}
}

const handleCreateOpportunities = async (input: CreateTestOpportunitiesInput) => {
	creatingOpportunities.value = true
	createOpportunitiesError.value = ""
	createdOpportunityDealIds.value = []
	error.value = ""

	try {
		const result = await createTestOpportunitiesApi(store.userApiToken, input)
		createdOpportunityDealIds.value = result.opportunityIds
		toastStore.success("adminPanelPage.users.createOpportunitiesSuccess")
	} catch (err: any) {
		console.error("Error creating opportunities:", err)
		createOpportunitiesError.value = err.message || "Failed to create opportunities"
	} finally {
		creatingOpportunities.value = false
	}
}

const showUserPermissions = async (user: any) => {
	selectedUser.value = user
	showPermissionsModal.value = true
	loadingPermissions.value = true
	userPermissions.value = []

	try {
		// Some roles mihgt have the overlapping permissions,
		// so as we go through the permissions, we keep the ids to ensure we won't add a permission twice
		const uniquePermissions: any[] = []
		const addedPermissionIds = new Set<number>()

		if (user.roles && user.roles.length > 0) {
			for (const role of user.roles) {
				if (role.permissions && role.permissions.length > 0) {
					for (const permission of role.permissions) {
						// Have we already added this permission? If no, then add the id and the permission.
						if (!addedPermissionIds.has(permission.id)) {
							uniquePermissions.push(permission)
							addedPermissionIds.add(permission.id)
						}
					}
				}
			}
		}

		userPermissions.value = uniquePermissions
	} catch (err: any) {
		console.error("Error loading user permissions:", err)
		error.value = err.message || "Failed to load user permissions"
	} finally {
		loadingPermissions.value = false
	}
}

const closePermissionsModal = () => {
	showPermissionsModal.value = false
	selectedUser.value = null
	userPermissions.value = []
}

const handleLoginAs = async (user: any) => {
	loggingInAs.value = user.id
	error.value = ""

	try {
		const result = await loginAsUser(store.userApiToken, Number(user.id))

		if (!result.success || !result.token) {
			error.value = result.error || "Failed to login as user"
			return
		}

		store.userApiToken = result.token
		store.CurrentUserDetails = null
		router.push("/")
	} catch (err: any) {
		console.error("Error logging in as user:", err)
		error.value = err.message || "Failed to login as user"
	} finally {
		loggingInAs.value = null
	}
}

const handleRevokeToken = async (user: any) => {
	const label = user.name || user.email || String(user.id)
	const confirmed = window.confirm(t("adminPanelPage.users.confirmRevokeToken", { user: label }))
	if (!confirmed) return

	const userId = user.id
	revokingUser.value = userId
	revokeSuccess.value = null

	try {
		await revokeUserToken(store.userApiToken, Number(userId))
		revokeSuccess.value = userId

		setTimeout(() => {
			if (revokeSuccess.value === userId) {
				revokeSuccess.value = null
			}
		}, 3000)
	} catch (err: any) {
		console.error("Error revoking user token:", err)
		error.value = err.message || "Failed to revoke user token"
	} finally {
		revokingUser.value = null
	}
}

const handleArchiveUser = async (user: any) => {
	const label = user.name || user.email || String(user.id)
	const confirmed = window.confirm(t("adminPanelPage.users.confirmArchiveUser", { user: label }))
	if (!confirmed) return

	archivingUser.value = user.id
	error.value = ""

	try {
		await archiveUser(store.userApiToken, Number(user.id))
		await loadUsers()
	} catch (err: any) {
		console.error("Error archiving user:", err)
		error.value = err.message || "Failed to archive user"
	} finally {
		archivingUser.value = null
	}
}

onMounted(async () => {
	if (!store.currentUser?.has(Permission.AuthorizationWrite)) {
		router.push("/")
		return
	}

	try {
		await Promise.all([loadUsers({ force: false }), loadRoles()])
	} catch (err) {
		console.error("Error initializing admin page:", err)
		error.value = "Failed to initialize admin page"
	} finally {
		loading.value = false
	}
})
</script>

<style scoped>
.loading,
.error {
	text-align: center;
	padding: var(--spacing-2xl);
	font-size: var(--font-size-medium);
	color: var(--neutral-600);
}

.error {
	color: var(--accent-warning);
}

.admin-users {
	display: flex;
	flex: 1;
	flex-direction: column;
	min-height: 0;
}

.admin-content {
	display: flex;
	flex: 1;
	flex-direction: column;
	min-height: 0;

	.users-section {
		display: flex;
		flex: 1;
		flex-direction: column;
		min-height: 0;

		.toolbar {
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			gap: var(--spacing-sm);
			margin-bottom: var(--spacing-md);

			.actions {
				display: flex;
				align-items: center;
				gap: var(--spacing-sm);
				margin-left: auto;
			}

			.search {
				display: flex;
				align-items: center;
				gap: var(--spacing-sm);
				flex: 0 1 360px;
				min-width: 200px;
				height: var(--size-button-s);
				box-sizing: border-box;
				padding: 0 var(--spacing-md);
				border: var(--border-width-thin) solid var(--neutral-200);
				border-radius: var(--border-radius-full);
				color: var(--neutral-600);

				&:focus-within {
					border-color: var(--primary-green);
				}

				input {
					flex: 1;
					min-width: 0;
					height: 100%;
					padding: 0;
					border: none;
					outline: none;
					background: none;
					font-family: inherit;
					font-size: var(--font-size-small);
					color: var(--neutral-900);
				}
			}
		}

		.users-grid {
			display: flex;
			flex-direction: column;
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-md);
			overflow: hidden;
			background: var(--neutral-0);
			flex: 0 1 auto;
			min-height: 0;
			overflow-y: auto;
			overflow-x: auto;

			.header {
				display: grid;
				grid-template-columns: minmax(260px, 340px) auto 140px 180px 140px;
				background-color: var(--neutral-50);
				border-bottom: var(--border-width-base) solid var(--neutral-200);
				position: sticky;
				top: 0;
				z-index: 10;
			}

			.row {
				display: grid;
				grid-template-columns: minmax(260px, 340px) auto 140px 180px 140px;
				border-bottom: var(--border-width-thin) solid var(--neutral-200);
				transition: background-color 0.2s ease;

				&:hover {
					background-color: var(--neutral-50);
				}

				&:last-child {
					border-bottom: none;
				}

				&.archived {
					opacity: 0.55;
					background-color: var(--neutral-50);

					&:hover {
						background-color: var(--neutral-100) !important;
					}
				}
			}

			.cell {
				padding: var(--space-16) var(--space-12);
				display: flex;
				align-items: center;
				min-height: 60px;

				&.header {
					min-height: auto;
					font-size: var(--font-size-small);
					font-weight: var(--font-weight-medium);
					color: var(--neutral-600);
					background-color: var(--neutral-50);
				}

				&.user {
					text-align: left;
					justify-content: flex-start;
					align-items: flex-start;

					.user-identity {
						display: flex;
						flex-direction: column;
						gap: var(--space-4);
						min-width: 0;
						width: 100%;

						.name {
							font-weight: var(--font-weight-medium);
							color: var(--neutral-900);
							line-height: 1.35;
							word-break: break-word;

							.extended-access-badge {
								display: inline-flex;
								align-items: center;
								margin-left: var(--space-4);
								padding: var(--spacing-xxs) var(--spacing-sm);
								border-radius: var(--border-radius-full);
								background-color: color-mix(in srgb, var(--primary-green) 12%, transparent);
								color: var(--primary-green);
								font-size: var(--font-size-xs);
								font-weight: var(--font-weight-semibold);
								vertical-align: middle;
							}
						}

						.email {
							font-size: var(--font-size-small);
							color: var(--neutral-600);
							line-height: 1.35;
							word-break: break-all;
						}
					}
				}

				&.date {
					text-align: right;
					justify-content: flex-start;
				}

				&.roles {
					display: flex;
					align-items: center;
					gap: var(--space-8);

					.roles-with-action {
						display: flex;
						align-items: center;
						gap: var(--space-8);
						width: 100%;

						.role-multiselect {
							width: 100%;
							min-width: 200px;
							max-width: 200px;
							--width-select-container: 208px;
						}

						.updating {
							color: var(--accent-info);
							font-style: italic;
							width: 36px;
							height: 36px;
							display: flex;
							align-items: center;
							justify-content: center;
							font-size: var(--font-size-small);
							flex-shrink: 0;
						}

						.success {
							color: var(--accent-success);
							font-weight: var(--font-weight-medium);
							width: 36px;
							height: 36px;
							display: flex;
							align-items: center;
							justify-content: center;
							font-size: var(--font-size-small);
							flex-shrink: 0;
						}

						.permissions-icon-btn {
							background: none;
							border: none;
							color: var(--neutral-600);
							cursor: pointer;
							padding: var(--space-8);
							border-radius: var(--border-radius-sm);
							transition: all 0.2s ease;
							display: flex;
							align-items: center;
							justify-content: center;
							min-width: 36px;
							height: 36px;

							&:hover:not(:disabled) {
								background-color: var(--neutral-100);
								color: var(--primary-green);
							}

							&:disabled {
								opacity: 0.5;
								cursor: not-allowed;
							}
						}
					}
				}

				&.action {
					display: flex;
					align-items: center;
					justify-content: center;

					.user-actions-wrap {
						display: flex;
						flex-direction: column;
						align-items: center;
						gap: var(--space-6);
						min-width: 0;

						.trigger {
							display: inline-flex;
							align-items: center;
							justify-content: center;
							gap: var(--space-8);
							min-height: 36px;
							padding: var(--space-6) var(--space-10);
							border: var(--border-width-thin) solid var(--neutral-200);
							border-radius: var(--border-radius-full);
							background: var(--neutral-0);
							color: var(--neutral-900);
							font-size: var(--font-size-small);
							font-weight: var(--font-weight-medium);
							cursor: pointer;
							transition:
								border-color 0.2s ease,
								box-shadow 0.2s ease;
							max-width: 100%;

							&:hover:not(:disabled) {
								border-color: var(--neutral-400);
								box-shadow: var(--shadow-sm);
							}

							&:focus-visible {
								outline: none;
								border-color: var(--accent-info);
								box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
							}

							&:disabled {
								opacity: 0.65;
								cursor: not-allowed;
							}

							.label {
								white-space: nowrap;
								overflow: hidden;
								text-overflow: ellipsis;
							}

							.chevron {
								width: var(--space-8);
								height: var(--space-8);
								flex-shrink: 0;
								border-right: var(--border-width-base) solid var(--neutral-600);
								border-bottom: var(--border-width-base) solid var(--neutral-600);
								transform: rotate(45deg);
								margin-top: -2px;
								transition: transform 0.2s ease;

								&.open {
									transform: rotate(225deg);
									margin-top: 2px;
								}
							}
						}

						.revoke-success-inline {
							font-size: var(--font-size-small);
							color: var(--accent-success);
							font-weight: var(--font-weight-medium);
							text-align: center;
							line-height: 1.2;
						}
					}
				}

				.empty {
					padding: var(--spacing-xl);
					text-align: center;
					color: var(--neutral-600);
				}
			}

			@media (max-width: 768px) {
				.header {
					display: none;
				}

				.row {
					grid-template-columns: minmax(0, 1fr) auto;
					grid-template-areas:
						"user action"
						"roles roles"
						"date date"
						"updated updated";
					gap: var(--spacing-sm);
					padding: var(--spacing-md);
				}

				.cell {
					padding: 0;
					min-height: auto;

					&.user {
						grid-area: user;
					}

					&.action {
						grid-area: action;
						align-items: flex-start;
						justify-content: flex-end;
					}

					&.roles {
						grid-area: roles;

						.roles-with-action .role-multiselect {
							flex: 1;
							max-width: none;
							--width-select-container: 100%;
						}
					}

					&.date {
						grid-area: date;
					}

					&.updated-by {
						grid-area: updated;
					}

					&.date,
					&.updated-by {
						font-size: var(--font-size-small);
						color: var(--neutral-600);

						&::before {
							content: attr(data-label) "\00a0: ";
							margin-right: var(--spacing-xs);
						}
					}
				}
			}
		}
	}
}

.user-actions-popover {
	min-width: 220px;
	padding: var(--space-6) 0;
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	box-shadow: var(--shadow-lg);
	z-index: 50;

	.item {
		display: block;
		width: 100%;
		padding: var(--space-10) var(--space-14);
		border: none;
		background: none;
		text-align: left;
		font-size: var(--font-size-base);
		color: var(--neutral-900);
		cursor: pointer;
		transition: background-color 0.15s ease;

		&:hover:not(:disabled) {
			background-color: var(--neutral-100);
		}

		&:disabled {
			opacity: 0.45;
			cursor: not-allowed;
		}

		&.danger:not(:disabled) {
			color: var(--accent-warning);
		}

		&.danger:hover:not(:disabled) {
			background-color: var(--neutral-100);
		}
	}
}

@media (max-width: 768px) {
	.admin-content .users-section .toolbar {
		.search,
		.create-user {
			flex: 1 1 100%;
			min-width: 0;
		}

		.actions {
			display: grid;
			flex: 1 1 100%;
			grid-template-columns: 1fr 1fr;
			gap: var(--spacing-xs);
			margin-left: 0;

			> * {
				width: 100%;
			}
		}
	}
}
</style>
